import React, { useMemo, useState } from 'react';
import StyleSettingsCarouselUploaderState from './styleSettingsCarouselUploaderState/StyleSettingsCarouselUploaderState';
import { TFileId, TLanguageIDs, ICustomGroups, TKeyForNewItemToCarousel, TMediaFileType } from 'types';
import { aspectRatiosForStyleSettings } from 'appConstants';
import { v4 as uuidv4 } from 'uuid';
import { isHideButtonInCarousel } from 'components/EmblaCarousel/isHideButtonInCarousel';
import { CropperModalNew } from './components/CropperModalNew';
import { ErrorModalsNew } from './components/ErrorModalsNew';
import { PDFBlockNew } from './components/PDFBlockNew';
import { UploaderNew } from './components/UploaderNew';

const imageMaxFileSizeMB = 1;
const pdfMaxFileSizeMB = 25;
const groupsWithPdf = ['how_it_works', 'free_right_button'];
const groupsWithSmallVideo = ['screen_saver_for_ordering', 'screen_saver_without_ordering'];

const getItemKey = (groupKey: string): TKeyForNewItemToCarousel => {
	switch (groupKey) {
		case 'how_it_works':
			return 'image_how_it_works';
		case 'free_right_button':
			return 'image_free_right_button';
		default:
			return 'image_screen_saver';
	}
};

interface IProps {
	group: ICustomGroups;
	languageId: TLanguageIDs;
	selectedLanguage: Nullable<TLanguageIDs>;
	deleteHandler: (languageId: Nullable<TLanguageIDs>, groupId: string, translationId: string) => void;
	addItemHandler: (
		languageId: Nullable<TLanguageIDs>,
		groupId: string,
		translationId: string,
		itemKey: TKeyForNewItemToCarousel,
		mediaFileType: TMediaFileType,
		sourceUrl: string,
		fileId: TFileId,
	) => void;
	showGroupLoader: () => void;
	hideGroupLoader: () => void;
	replacePdfToCarousel: ({
		translationIdOfItemForDeletion,
		languageId,
		groupId,
		translationId,
		itemKey,
		fileId,
	}: {
		translationIdOfItemForDeletion: string | undefined;
		languageId: TLanguageIDs;
		groupId: string;
		translationId: string;
		itemKey: TKeyForNewItemToCarousel;
		fileId: TFileId;
	}) => void;
}

export const StyleSettingsCarouselUploaderNew: React.FC<IProps> = ({
	group,
	languageId,
	selectedLanguage,
	deleteHandler,
	addItemHandler,
	showGroupLoader,
	hideGroupLoader,
	replacePdfToCarousel,
}) => {
	const [selectedIndex, setSelectedIndex] = useState<number>(0);
	const items = useMemo(() => group.items.filter((el) => el.fileId !== null), [group]);
	const mediaItems = useMemo(() => items.filter((el) => el.mediaFileType === 1 || el.mediaFileType === 2), [items]);
	const pdfItem = items.find((el) => el.mediaFileType === 3 && (!!el.filePath || !!el.fileId));
	const groupWithActualItems = { ...group, items };
	const videoMaxFileSizeMB = groupsWithSmallVideo.includes(group.key) ? 5 : 25;
	const itemKey = getItemKey(group.key);

	const handleDeleteItem = (translationId: string) => {
		deleteHandler(languageId, group.id, translationId);
	};

	const handleUploadItem = ({
		mediaFileType,
		sourceUrl,
		fileId,
	}: {
		mediaFileType: TMediaFileType;
		sourceUrl: string;
		fileId: string;
	}) => {
		addItemHandler(languageId, group.id, uuidv4(), itemKey, mediaFileType, sourceUrl, fileId);
	};

	const getPdfTranslationId = () => items.find((el) => el.mediaFileType === 3)?.translationId;

	const handleUploadPdf = (fileId: string) => {
		replacePdfToCarousel({
			translationIdOfItemForDeletion: getPdfTranslationId(),
			languageId,
			groupId: group.id,
			translationId: uuidv4(),
			itemKey,
			fileId,
		});
	};

	const handleDeletePdf = () => {
		const translationIdOfItemForDeletion = getPdfTranslationId();
		replacePdfToCarousel({
			translationIdOfItemForDeletion,
			languageId,
			groupId: group.id,
			translationId: translationIdOfItemForDeletion || '',
			itemKey,
			fileId: null,
		});
	};

	return (
		<StyleSettingsCarouselUploaderState>
			<div className="flex flex-col gap-4">
				<UploaderNew
					mediaItems={mediaItems}
					selectedIndex={selectedIndex}
					isMakeRequest={selectedLanguage === languageId}
					isImageUploadHidden={isHideButtonInCarousel(groupWithActualItems, 1)}
					isVideoUploadHidden={isHideButtonInCarousel(groupWithActualItems, 2)}
					videoMaxFileSizeMB={videoMaxFileSizeMB}
					onSelectedIndexChange={setSelectedIndex}
					onDeleteItem={handleDeleteItem}
					onUploadVideo={({ sourceUrl, fileId }) => handleUploadItem({ mediaFileType: 2, sourceUrl, fileId })}
					showGroupLoader={showGroupLoader}
					hideGroupLoader={hideGroupLoader}
				/>
				{groupsWithPdf.includes(group.key) && (
					<PDFBlockNew
						pdfItem={pdfItem}
						maxFileSizeMB={pdfMaxFileSizeMB}
						onUpload={handleUploadPdf}
						onDelete={handleDeletePdf}
					/>
				)}
			</div>
			<CropperModalNew
				titleKey={group.key}
				orientationRadioName={`orientation-${group.id}-${languageId}`}
				horizontalAspectRatio={aspectRatiosForStyleSettings[group.key]?.horizontal}
				verticalAspectRatio={aspectRatiosForStyleSettings[group.key]?.vertical}
				maxFileSizeMB={imageMaxFileSizeMB}
				showGroupLoader={showGroupLoader}
				hideGroupLoader={hideGroupLoader}
				onUploadItem={({ sourceUrl, fileId }) => handleUploadItem({ mediaFileType: 1, sourceUrl, fileId })}
			/>
			<ErrorModalsNew
				imageMaxFileSizeMB={imageMaxFileSizeMB}
				videoMaxFileSizeMB={videoMaxFileSizeMB}
				pdfMaxFileSizeMB={pdfMaxFileSizeMB}
			/>
		</StyleSettingsCarouselUploaderState>
	);
};
